import { KLineData } from 'klinecharts';
import { klineLineStyle, lineStyleToKLine } from './lineStyle';

interface ADXIndicator {
  name: string;
  shortName: string;
  calcParams: any[];
  series: 'price' | 'volume' | 'normal';
  figures: Array<{
    key: string;
    title: string;
    type: string;
  }>;
  regenerateFigures?: (params: any[]) => Array<{
    key: string;
    title: string;
    type: string;
  }>;
  calc: (
    dataList: KLineData[],
    indicator: {
      calcParams: any[];
      figures: any[];
    }
  ) => any[];
  minValue?: number;
  maxValue?: number;
  styles?: unknown;
}

// Helper function to calculate True Range
const calculateTR = (current: KLineData, previous: KLineData): number => {
  const highLow = current.high - current.low;
  const highPrevClose = Math.abs(current.high - previous.close);
  const lowPrevClose = Math.abs(current.low - previous.close);
  return Math.max(highLow, highPrevClose, lowPrevClose);
};

export const createADX = (settings: any): ADXIndicator => {
  const values = settings?.values ?? settings ?? {};
  const period = values.Period || 14;
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const adxColor = styles.adx?.color ?? '#FFA500';
  const plusColor = styles.diplus?.color ?? '#00FF00';
  const minusColor = styles.diminus?.color ?? '#FF0000';
  const adxLineStyle = lineStyleToKLine(styles.adx?.style);
  const plusLineStyle = lineStyleToKLine(styles.diplus?.style);
  const minusLineStyle = lineStyleToKLine(styles.diminus?.style);

  return {
    name: 'adx', // Changed from 'ADX' to match indicatorId
    shortName: `ADX${period}`,
    calcParams: [period],
    series: 'normal',
    minValue: 0,
    maxValue: 100,
    figures: [
      {
        key: 'adx',
        title: `ADX${period}: `,
        type: 'line',
      },
      {
        key: 'plusDI',
        title: '+DI: ',
        type: 'line',
      },
      {
        key: 'minusDI',
        title: '-DI: ',
        type: 'line',
      },
    ],
    styles: {
      lines: [
        klineLineStyle(adxColor, 2, adxLineStyle),
        klineLineStyle(plusColor, 1, plusLineStyle),
        klineLineStyle(minusColor, 1, minusLineStyle),
      ],
    },
    regenerateFigures: (params: any[]) => {
      const [period] = params;
      return [
        {
          key: 'adx',
          title: `ADX${period}: `,
          type: 'line',
        },
        {
          key: 'plusDI',
          title: '+DI: ',
          type: 'line',
        },
        {
          key: 'minusDI',
          title: '-DI: ',
          type: 'line',
        },
      ];
    },
    /**
     * Single-pass O(N) ADX.
     *
     * Wilder's smoothing is recurrent: smooth[i] = (smooth[i-1] * (p-1) + x[i]) / p
     * after the first window is seeded with a simple average of the first `p` values.
     * Keeping running accumulators avoids the previous O(N²) implementation that
     * re-ran wildersSmoothing(...) over the full series at every bar.
     */
    calc: (dataList, indicator) => {
      const { calcParams = [period] } = indicator;
      const [currentPeriod] = calcParams as [number];
      const p = currentPeriod;
      const n = dataList.length;
      const results: Array<{ adx?: number; plusDI?: number; minusDI?: number }> =
        new Array(n);

      let trSeedSum = 0;
      let plusDMSeedSum = 0;
      let minusDMSeedSum = 0;
      let trSmoothed = 0;
      let plusDMSmoothed = 0;
      let minusDMSmoothed = 0;
      let dxSeedSum = 0;
      let adxSmoothed = 0;
      let trSeeded = false;
      let dxCount = 0;

      for (let i = 0; i < n; i++) {
        if (i === 0) {
          results[i] = {};
          continue;
        }

        const kLineData = dataList[i];
        const previous = dataList[i - 1];

        const tr = calculateTR(kLineData, previous);
        const upMove = kLineData.high - previous.high;
        const downMove = previous.low - kLineData.low;
        const plusDM = upMove > downMove && upMove > 0 ? upMove : 0;
        const minusDM = downMove > upMove && downMove > 0 ? downMove : 0;

        const idxFromOne = i; // 1-based offset relative to dataList[0]
        const out: { adx?: number; plusDI?: number; minusDI?: number } = {};

        if (idxFromOne < p) {
          trSeedSum += tr;
          plusDMSeedSum += plusDM;
          minusDMSeedSum += minusDM;
          results[i] = out;
          continue;
        }

        if (!trSeeded) {
          trSeedSum += tr;
          plusDMSeedSum += plusDM;
          minusDMSeedSum += minusDM;
          trSmoothed = trSeedSum / p;
          plusDMSmoothed = plusDMSeedSum / p;
          minusDMSmoothed = minusDMSeedSum / p;
          trSeeded = true;
        } else {
          trSmoothed = (trSmoothed * (p - 1) + tr) / p;
          plusDMSmoothed = (plusDMSmoothed * (p - 1) + plusDM) / p;
          minusDMSmoothed = (minusDMSmoothed * (p - 1) + minusDM) / p;
        }

        if (trSmoothed === 0) {
          results[i] = out;
          continue;
        }

        const plusDI = (plusDMSmoothed / trSmoothed) * 100;
        const minusDI = (minusDMSmoothed / trSmoothed) * 100;
        out.plusDI = plusDI;
        out.minusDI = minusDI;

        const diSum = plusDI + minusDI;
        const dx = diSum !== 0 ? (Math.abs(plusDI - minusDI) / diSum) * 100 : 0;
        dxCount += 1;

        if (dxCount < p) {
          dxSeedSum += dx;
        } else if (dxCount === p) {
          dxSeedSum += dx;
          adxSmoothed = dxSeedSum / p;
          out.adx = adxSmoothed;
        } else {
          adxSmoothed = (adxSmoothed * (p - 1) + dx) / p;
          out.adx = adxSmoothed;
        }

        results[i] = out;
      }

      return results;
    },
  };
};